<template>
  <div class="evidence-header gm-card rounded-xl pa-5 mb-4 border">
    <!-- 第一层：顶栏告警名称与法定结论核心展示 -->
    <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-4">
      <div class="d-flex align-center ga-3">
        <div class="alarm-icon-box d-flex align-center justify-center rounded-xl" :class="verdictTheme.iconBoxClass">
          <v-icon size="26" :color="verdictTheme.iconColor">{{ verdictTheme.icon }}</v-icon>
        </div>
        <div>
          <div class="d-flex flex-wrap align-center ga-2 mb-1">
            <h2 class="text-h6 font-weight-bold text-slate-800 mb-0">
              {{ displayAlarmName }}
            </h2>
            <span class="alarm-code-tag px-2 py-0-5 text-caption rounded font-weight-bold">{{ alarmCode }}</span>
            <span v-if="severityText" class="severity-tag px-2 py-0-5 text-caption rounded font-weight-bold">
              {{ severityText }}
            </span>
            <span v-if="eventStatusText" class="status-chip px-2 py-0-5 text-caption rounded font-weight-medium" :class="eventStatusClass">
              {{ eventStatusText }}
            </span>
          </div>
          <div class="text-caption text-slate-500 d-flex flex-wrap align-center ga-3">
            <span v-if="stationName">电站：<strong class="text-slate-700">{{ stationName }}</strong></span>
            <span v-if="stationId">电站ID：<span class="text-slate-700 font-weight-medium">{{ stationId }}</span></span>
            <span>设备UUID：<span class="text-slate-700 font-weight-medium">{{ equipmentUuid || '—' }}</span></span>
            <span v-if="alarmId">告警ID：<span class="text-slate-700 font-weight-medium">{{ alarmId }}</span></span>
          </div>
        </div>
      </div>

      <!-- 法定结论 Badge（必须原样使用后端法定文案，严禁出现误报字样） -->
      <div class="d-flex flex-column align-end">
        <div class="verdict-pill px-4 py-2 rounded-xl d-flex align-center ga-2" :class="verdictTheme.badgeClass">
          <v-icon size="18" :color="verdictTheme.iconColor">{{ verdictTheme.badgeIcon }}</v-icon>
          <span class="font-weight-bold text-body-2">{{ displayVerdictLabel }}</span>
        </div>
        <div class="text-caption text-disabled mt-1-5 d-flex align-center ga-2">
          <span v-if="elapsedMs != null">诊断耗时 {{ elapsedMs }}ms</span>
          <span v-if="isPartial" class="badge-partial text-amber-darken-3 font-weight-medium">确定性规则生成</span>
          <span v-if="analysisId" class="text-disabled">ID: {{ analysisId.slice(0, 16) }}</span>
        </div>
      </div>
    </div>

    <!-- 第二层：核心事实研判横幅（把 impactNarrative 置顶醒目展示） -->
    <div v-if="impactNarrative" class="verdict-banner pa-3-5 rounded-lg mb-4 d-flex align-start ga-3" :class="verdictTheme.bannerClass">
      <v-icon :color="verdictTheme.iconColor" size="20" class="mt-0-5 flex-shrink-0">
        {{ verdictTheme.bannerIcon }}
      </v-icon>
      <div class="flex-grow-1">
        <div class="d-flex align-center justify-space-between mb-0-5">
          <span class="font-weight-bold text-body-2" :class="verdictTheme.bannerTitleClass">
            {{ verdictTheme.bannerTitle }}
          </span>
          <span v-if="verdictReason" class="text-caption text-slate-500 font-weight-normal">
            {{ verdictReason }}
          </span>
        </div>
        <div class="text-body-2 text-slate-700 font-weight-medium lh-base">
          {{ impactNarrative }}
        </div>
      </div>
    </div>

    <!-- 第三层：取证数据源与执行状态指标条 -->
    <div class="d-flex flex-wrap align-center justify-space-between ga-3 pt-3 border-t text-caption">
      <!-- 左侧：多源超级表取证健康度 -->
      <div class="d-flex flex-wrap align-center ga-2">
        <span class="text-slate-500 font-weight-medium">多源数据探针：</span>
        <span class="probe-badge px-2 py-0-5 rounded bg-slate-100 text-slate-700 font-weight-medium">
          探针 {{ probesCount }} 次 / 查询 {{ queriesCount }} 次
        </span>
        <span
          v-for="src in sourceBadges"
          :key="src.stable"
          class="source-badge px-2 py-0-5 rounded d-inline-flex align-center ga-1 font-weight-medium"
          :class="src.isOk ? 'src-ok' : 'src-warn'"
        >
          <span class="dot" :class="src.isOk ? 'dot-green' : 'dot-amber'" />
          {{ src.label }} ({{ src.rowCount }}行)
        </span>
      </div>

      <!-- 右侧：判定时间窗口 -->
      <div class="d-flex align-center ga-4 text-slate-600">
        <div v-if="alarmTime">
          告警时刻：<strong class="text-slate-800">{{ alarmTime }}</strong>
        </div>
        <div v-if="windowRange" class="d-flex align-center ga-1">
          <v-icon size="14" class="text-slate-400">mdi-clock-time-four-outline</v-icon>
          <span>取证判定窗口(±30m)：</span>
          <span class="text-slate-800 font-weight-bold">{{ windowRange }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  alarmRecord: { type: Object, default: () => ({}) },
  evidence: { type: Object, default: null },
  diagnosis: { type: Object, default: null },
  steps: { type: Object, default: () => ({}) },
  status: { type: String, default: 'idle' },
  elapsedMs: { type: Number, default: null },
  analysisId: { type: String, default: '' },
})

const alarmCode = computed(() => {
  return props.evidence?.alarmCode || props.alarmRecord?.alarmCode || props.alarmRecord?.code || '—'
})

const displayAlarmName = computed(() => {
  return props.evidence?.ruleName || props.alarmRecord?.description || props.alarmRecord?.alarmName || '告警证据链诊断'
})

const alarmId = computed(() => {
  return props.evidence?.alarmId || props.alarmRecord?.id || props.alarmRecord?.alarmId || props.alarmRecord?.key || ''
})

const severityText = computed(() => {
  const lvl = props.alarmRecord?.level ?? props.alarmRecord?.severityLevel
  if (lvl != null && lvl !== '--') {
    return `等级 ${lvl}`
  }
  return ''
})

const eventStatusText = computed(() => {
  const st = props.alarmRecord?.eventStatus
  if (st === 'active') return '未恢复'
  if (st === 'recovered') return '已恢复'
  return ''
})

const eventStatusClass = computed(() => {
  return props.alarmRecord?.eventStatus === 'recovered' ? 'status-recovered' : 'status-active'
})

const stationName = computed(() => props.alarmRecord?.stationName || '')
const stationId = computed(() => props.evidence?.stationId || props.alarmRecord?.stationId || '')
const equipmentUuid = computed(() => props.evidence?.equipmentUuid || props.alarmRecord?.uuid || props.alarmRecord?.deviceVid || '')
const alarmTime = computed(() => props.evidence?.alarmTime || props.alarmRecord?.lastTime || props.alarmRecord?.startTime || '')

const windowRange = computed(() => {
  const w = props.evidence?.window
  if (!w || !w.start || !w.end) return null
  return `${w.start.replace('T', ' ').slice(11, 19)} ~ ${w.end.replace('T', ' ').slice(11, 19)}`
})

const isPartial = computed(() => props.status === 'partial')

// 核心影响结论
const impactNarrative = computed(() => {
  return props.diagnosis?.impactNarrative || props.evidence?.reason || ''
})

const verdictReason = computed(() => {
  return props.evidence?.reason || ''
})

// 探针与超级表统计
const probesCount = computed(() => props.evidence?.probesIssued ?? 4)
const queriesCount = computed(() => props.evidence?.queriesIssued ?? 4)

const sourceBadges = computed(() => {
  const list = props.evidence?.sources || []
  return list.map(s => ({
    stable: s.stable,
    label: s.label || s.stable,
    rowCount: s.rowCount ?? 0,
    isOk: s.status === 'ok',
  }))
})

// 法定 verdict 标签：必须原样使用后端法定表述
const displayVerdictLabel = computed(() => {
  if (props.evidence?.verdictLabel) return props.evidence.verdictLabel
  const v = props.evidence?.verdict || props.diagnosis?.verdict
  if (!v) return '正在进行证据链取证...'

  switch (v) {
    case 'triggered':
      return '触发条件已在云端数据中复现（告警已确证）'
    case 'not_reproduced':
      return '触发条件未在云端历史数据中复现（不等于误报）'
    case 'inconclusive':
      return '证据不足，无法判断触发条件是否复现'
    case 'unsupported':
      return '本期未配置该告警码的证据链规则，未做取证'
    case 'unresolved':
      return '设备坐标无法解析，未做取证'
    default:
      return v
  }
})

// 法定红线主题配色：严禁 not_reproduced 用绿色
const verdictTheme = computed(() => {
  const v = props.evidence?.verdict || props.diagnosis?.verdict
  switch (v) {
    case 'triggered':
      return {
        badgeClass: 'verdict-triggered',
        iconBoxClass: 'icon-box-triggered',
        bannerClass: 'banner-triggered',
        bannerTitleClass: 'text-red-darken-3',
        bannerTitle: '【告警确证】触发条件已复现',
        icon: 'mdi-alert-octagon',
        badgeIcon: 'mdi-alert-octagon',
        bannerIcon: 'mdi-alert-circle',
        iconColor: '#e53935',
      }
    case 'not_reproduced':
      return {
        badgeClass: 'verdict-not-reproduced',
        iconBoxClass: 'icon-box-not-reproduced',
        bannerClass: 'banner-not-reproduced',
        bannerTitleClass: 'text-indigo-darken-3',
        bannerTitle: '【未在云端复现】仍须现场核查 · 绝非误报',
        icon: 'mdi-shield-alert-outline',
        badgeIcon: 'mdi-alert-circle-outline',
        bannerIcon: 'mdi-information',
        iconColor: '#4f46e5',
      }
    case 'inconclusive':
      return {
        badgeClass: 'verdict-inconclusive',
        iconBoxClass: 'icon-box-inconclusive',
        bannerClass: 'banner-inconclusive',
        bannerTitleClass: 'text-amber-darken-3',
        bannerTitle: '【证据不足】部分关键测点缺失',
        icon: 'mdi-help-rhombus-outline',
        badgeIcon: 'mdi-help-circle-outline',
        bannerIcon: 'mdi-help-circle',
        iconColor: '#d97706',
      }
    case 'unsupported':
    case 'unresolved':
    default:
      return {
        badgeClass: 'verdict-unsupported',
        iconBoxClass: 'icon-box-unsupported',
        bannerClass: 'banner-unsupported',
        bannerTitleClass: 'text-slate-700',
        bannerTitle: '【未做取证】',
        icon: 'mdi-information-outline',
        badgeIcon: 'mdi-information-outline',
        bannerIcon: 'mdi-information',
        iconColor: '#64748b',
      }
  }
})
</script>

<style scoped lang="scss">
.evidence-header {
  background: #ffffff;
  border-color: #e2e8f0;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
}

.alarm-icon-box {
  width: 52px;
  height: 52px;
  transition: all 0.2s;
}

.alarm-code-tag {
  background: #f1f5f9;
  color: #1e293b;
  font-size: 12px;
  border: 1px solid #cbd5e1;
}

.severity-tag {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.status-chip {
  &.status-active {
    background: #fff7ed;
    color: #ea580c;
    border: 1px solid #ffedd5;
  }
  &.status-recovered {
    background: #f0fdf4;
    color: #16a34a;
    border: 1px solid #dcfce7;
  }
}

/* 法定结论 Badge 配色体系 */
.verdict-pill {
  border: 1px solid transparent;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.verdict-triggered {
  background: #fef2f2;
  border-color: #fecaca;
  color: #b91c1c;
}
.icon-box-triggered {
  background: #fee2e2;
}
.banner-triggered {
  background: #fff1f2;
  border: 1px solid #fecdd3;
}

/* not_reproduced 严禁使用绿色，使用深邃蓝紫色体系 */
.verdict-not-reproduced {
  background: #eef2ff;
  border-color: #c7d2fe;
  color: #3730a3;
}
.icon-box-not-reproduced {
  background: #e0e7ff;
}
.banner-not-reproduced {
  background: #eef2ff;
  border: 1px solid #c7d2fe;
}

.verdict-inconclusive {
  background: #fffbeb;
  border-color: #fde68a;
  color: #b45309;
}
.icon-box-inconclusive {
  background: #fef3c7;
}
.banner-inconclusive {
  background: #fffbeb;
  border: 1px solid #fde68a;
}

.verdict-unsupported {
  background: #f8fafc;
  border-color: #e2e8f0;
  color: #475569;
}
.icon-box-unsupported {
  background: #f1f5f9;
}
.banner-unsupported {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.border-t {
  border-top: 1px solid #f1f5f9;
}

.probe-badge {
  border: 1px solid #e2e8f0;
}

.source-badge {
  border: 1px solid transparent;

  &.src-ok {
    background: #f0fdf4;
    color: #15803d;
    border-color: #bbf7d0;
  }

  &.src-warn {
    background: #fffbeb;
    color: #b45309;
    border-color: #fde68a;
  }

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    display: inline-block;

    &.dot-green {
      background: #22c55e;
    }
    &.dot-amber {
      background: #f59e0b;
    }
  }
}

.badge-partial {
  background: #fffbeb;
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid #fde68a;
}
</style>
